import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/** Quem mandou a última mensagem da conversa (espelha `ChatSenderType` do backend). */
export type ChatSender = 'CLIENT' | 'AI' | 'TEAM' | 'AUTOMATION'

export type ChatInteraction =
    | 'INBOUND'
    | 'OUTBOUND_UNANSWERED'
    | 'OUTBOUND_ANSWERED'

export type ChatDateField = 'createdAt' | 'lastMessageAt'

export interface ChatFilters {
    search?: string
    /** `ALL` = a aba "Todos". Precisa existir na URL: sem o valor, a tela voltaria ao padrão `OPEN`. */
    status?: 'OPEN' | 'CLOSED' | 'ALL'
    scope?: 'mine' | 'triage' | 'all'
    folderId?: number
    departmentId?: number
    /** Agente de IA da conversa. */
    attendantId?: number
    /** Pessoa da equipe dona do contato (carteira, lead ou oportunidade). */
    responsibleUserId?: number
    channel?: string[]
    lastSender?: ChatSender[]
    lastMessageUserId?: number
    interaction?: ChatInteraction[]
    unanswered?: boolean
    unread?: boolean
    waitingForHuman?: boolean
    pipelineId?: number[]
    stageId?: number[]
    tags?: string[]
    dateField?: ChatDateField
    dateFrom?: string
    dateTo?: string
}

/** Campos do painel — fora ficam busca, status, escopo, pasta e setor, que têm controle próprio na tela. */
export const PANEL_KEYS: (keyof ChatFilters)[] = [
    'attendantId',
    'responsibleUserId',
    'channel',
    'lastSender',
    'lastMessageUserId',
    'interaction',
    'unanswered',
    'unread',
    'waitingForHuman',
    'pipelineId',
    'stageId',
    'tags',
    'dateField',
    'dateFrom',
    'dateTo',
]

const NUMBER_KEYS: (keyof ChatFilters)[] = [
    'folderId',
    'departmentId',
    'attendantId',
    'responsibleUserId',
    'lastMessageUserId',
]
const NUMBER_LIST_KEYS: (keyof ChatFilters)[] = ['pipelineId', 'stageId']
const STRING_LIST_KEYS: (keyof ChatFilters)[] = [
    'channel',
    'lastSender',
    'interaction',
    'tags',
]
const BOOLEAN_KEYS: (keyof ChatFilters)[] = [
    'unanswered',
    'unread',
    'waitingForHuman',
]

/** Quantos filtros do painel estão ativos — é o número no badge do botão. */
export function countActiveFilters(filters: ChatFilters): number {
    return PANEL_KEYS.filter(key => {
        // A data conta uma vez só: `dateField` sem intervalo não filtra nada.
        if (key === 'dateField' || key === 'dateTo') return false
        const value = filters[key]
        if (value === undefined || value === null || value === '') return false
        if (Array.isArray(value)) return value.length > 0
        return true
    }).length
}

/** Só o que tem valor vira query: `undefined` no backend significa "todos". */
export function toQueryParams(filters: ChatFilters): Record<string, unknown> {
    const params: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === '') continue
        // "Todos" é a AUSÊNCIA do filtro para o backend.
        if (key === 'status' && value === 'ALL') continue
        if (Array.isArray(value)) {
            if (value.length === 0) continue
            params[key] = value.join(',')
        } else {
            params[key] = value
        }
    }
    return params
}

function parseFromSearchParams(searchParams: URLSearchParams): ChatFilters {
    const filters: ChatFilters = {}

    const read = (key: string) => searchParams.get(key) ?? undefined

    if (read('search')) filters.search = read('search')
    const status = read('status')
    if (status === 'OPEN' || status === 'CLOSED' || status === 'ALL') filters.status = status
    const scope = read('scope')
    if (scope === 'mine' || scope === 'triage' || scope === 'all') filters.scope = scope
    const dateField = read('dateField')
    if (dateField === 'createdAt' || dateField === 'lastMessageAt') {
        filters.dateField = dateField
    }
    if (read('dateFrom')) filters.dateFrom = read('dateFrom')
    if (read('dateTo')) filters.dateTo = read('dateTo')

    for (const key of NUMBER_KEYS) {
        const raw = read(key)
        const parsed = raw === undefined ? NaN : Number(raw)
        if (Number.isInteger(parsed)) (filters as any)[key] = parsed
    }
    for (const key of NUMBER_LIST_KEYS) {
        const raw = read(key)
        if (!raw) continue
        const list = raw.split(',').map(Number).filter(Number.isInteger)
        if (list.length > 0) (filters as any)[key] = list
    }
    for (const key of STRING_LIST_KEYS) {
        const raw = read(key)
        if (!raw) continue
        const list = raw.split(',').map(item => item.trim()).filter(Boolean)
        if (list.length > 0) (filters as any)[key] = list
    }
    for (const key of BOOLEAN_KEYS) {
        const raw = read(key)
        if (raw === 'true') (filters as any)[key] = true
        else if (raw === 'false') (filters as any)[key] = false
    }

    return filters
}

/** Tudo o que é recorte da lista, na ordem em que vai para a URL. */
const FILTER_KEYS: (keyof ChatFilters)[] = [
    'search',
    'status',
    'scope',
    'folderId',
    'departmentId',
    ...PANEL_KEYS,
]

/**
 * O que fica guardado no navegador entre uma visita e outra.
 *
 * A busca fica de fora de propósito: o texto digitado é coisa do momento, e uma
 * lista que reabre vazia por causa de uma busca esquecida parece defeito. O campo
 * de busca também nasce do valor inicial e, se a busca fosse restaurada depois,
 * o próprio campo vazio a apagaria.
 */
const PERSISTED_KEYS = FILTER_KEYS.filter(key => key !== 'search')

function toSearchParams(filters: ChatFilters, keys: (keyof ChatFilters)[]): URLSearchParams {
    const params = new URLSearchParams()
    for (const key of keys) {
        const value = filters[key]
        const empty =
            value === undefined ||
            value === null ||
            value === '' ||
            (Array.isArray(value) && value.length === 0)
        if (!empty) params.set(key, Array.isArray(value) ? value.join(',') : String(value))
    }
    return params
}

/**
 * Chave por pessoa E por empresa: pasta e setor são da empresa, e o recorte de
 * uma filial aplicado em outra filtraria por uma pasta que lá não existe. Também
 * evita que quem usa o mesmo computador herde o filtro do colega.
 */
function useFiltersStorageKey(variant: 'page' | 'dock'): string | null {
    const { user } = useAuth()
    if (!user?.id) return null
    return `atendimento:chat-filters:${variant}:${user.id}:${user.companyId ?? 0}`
}

/** Guardado como query string para passar pela MESMA validação da URL. */
function readSavedFilters(storageKey: string): ChatFilters | null {
    try {
        const raw = localStorage.getItem(storageKey)
        if (raw === null) return null
        const saved = parseFromSearchParams(new URLSearchParams(raw))
        delete saved.search
        return saved
    } catch {
        return null
    }
}

function writeSavedFilters(storageKey: string, filters: ChatFilters): void {
    try {
        localStorage.setItem(storageKey, toSearchParams(filters, PERSISTED_KEYS).toString())
    } catch {
        // Navegador sem armazenamento (aba anônima, cota cheia): o filtro só não sobrevive.
    }
}

/**
 * Filtros da lista de atendimentos guardados na URL.
 *
 * Na URL, e não em `useState`, porque a tela é aberta e fechada o tempo todo: quem
 * filtra "sem resposta", entra numa conversa e volta, perdia o filtro e recomeçava a
 * triagem do zero. Na URL o botão voltar funciona, o link pode ser mandado ao colega
 * e o F5 não apaga nada.
 *
 * A URL sozinha não bastava: entrar pelo menu abre `/atendimento/chats` sem query,
 * e o recorte sumia. Por isso o filtro também é guardado no navegador e volta
 * quando a tela abre sem nenhum filtro no endereço. Um link com filtro continua
 * mandando: o que vem na URL vence o que estava guardado.
 */
export function useChatFilters(): {
    filters: ChatFilters
    setFilters: (next: ChatFilters) => void
    patchFilters: (patch: Partial<ChatFilters>) => void
    clearPanelFilters: () => void
} {
    const [searchParams, setSearchParams] = useSearchParams()

    const filters = useMemo(
        () => parseFromSearchParams(searchParams),
        [searchParams],
    )

    const setFilters = useCallback(
        (next: ChatFilters) => {
            setSearchParams(
                prev => {
                    const params = new URLSearchParams(prev)
                    for (const key of FILTER_KEYS) {
                        const value = next[key]
                        const empty =
                            value === undefined ||
                            value === null ||
                            value === '' ||
                            (Array.isArray(value) && value.length === 0)
                        if (empty) params.delete(key)
                        else params.set(key, Array.isArray(value) ? value.join(',') : String(value))
                    }
                    // Trocar o filtro sempre volta para a primeira página: a página 4
                    // do recorte anterior não existe no recorte novo.
                    params.delete('page')
                    return params
                },
                { replace: true },
            )
        },
        [setSearchParams],
    )

    const storageKey = useFiltersStorageKey('page')
    const restoreChecked = useRef(false)

    useEffect(() => {
        if (!storageKey) return
        // Na primeira passada, decide se restaura; gravar antes disso trocaria o
        // recorte guardado pelo endereço vazio de quem chegou pelo menu.
        if (!restoreChecked.current) {
            restoreChecked.current = true
            const urlHasFilters = FILTER_KEYS.some(key => searchParams.has(key))
            const saved = urlHasFilters ? null : readSavedFilters(storageKey)
            if (saved && Object.keys(saved).length > 0) {
                setFilters(saved)
                return
            }
        }
        writeSavedFilters(storageKey, filters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, storageKey])

    const patchFilters = useCallback(
        (patch: Partial<ChatFilters>) => setFilters({ ...filters, ...patch }),
        [filters, setFilters],
    )

    const clearPanelFilters = useCallback(() => {
        const cleared: ChatFilters = { ...filters }
        for (const key of PANEL_KEYS) delete cleared[key]
        setFilters(cleared)
    }, [filters, setFilters])

    return { filters, setFilters, patchFilters, clearPanelFilters }
}

/**
 * Mesma interface de `useChatFilters`, só que em memória.
 *
 * O painel de conversas acoplado ao menu (o dock) aparece sobre QUALQUER tela do
 * sistema. Se os filtros dele fossem para a URL, filtrar a fila enquanto se
 * trabalha no financeiro sujaria o endereço daquela tela — e um `?status=OPEN`
 * herdado quebraria o link ao ser compartilhado. Na tela cheia de atendimentos o
 * recorte continua na URL, que é onde ele tem serventia.
 *
 * Guardado no navegador, separado do da tela cheia: o dock costuma ser usado
 * para outra fila (só as minhas, por exemplo) enquanto a tela cheia fica no geral.
 */
export function useChatFiltersMemory(): {
    filters: ChatFilters
    setFilters: (next: ChatFilters) => void
    patchFilters: (patch: Partial<ChatFilters>) => void
    clearPanelFilters: () => void
} {
    const storageKey = useFiltersStorageKey('dock')
    const [filters, setFilters] = useState<ChatFilters>(
        () => (storageKey ? readSavedFilters(storageKey) : null) ?? {},
    )

    useEffect(() => {
        if (storageKey) writeSavedFilters(storageKey, filters)
    }, [filters, storageKey])

    const patchFilters = useCallback((patch: Partial<ChatFilters>) => {
        setFilters(prev => {
            const next: ChatFilters = { ...prev, ...patch }
            for (const key of Object.keys(patch) as (keyof ChatFilters)[]) {
                if (next[key] === undefined) delete next[key]
            }
            return next
        })
    }, [])

    const clearPanelFilters = useCallback(() => {
        setFilters(prev => {
            const cleared: ChatFilters = { ...prev }
            for (const key of PANEL_KEYS) delete cleared[key]
            return cleared
        })
    }, [])

    return { filters, setFilters, patchFilters, clearPanelFilters }
}
